import {
  CHASSIS_SIZE_OPTIONS,
  existingSystemComponentTypeProperties
} from 'data/constants';
import {
  ElectricalPanelType,
  ExistingSystemComponentType,
  ExistingSystemType,
  GridObjectType,
  LevelType
} from 'data/types';
import {
  compositionHasEquipmentType,
  compositionWantsPackagedUnit
} from 'utils/composition-picks';
import { isEmpty } from 'utils/helpers';
import {
  DuctedFuelToken,
  systemTypeCodeFuel
} from 'utils/kept-system-type-annotation';
import { CompositionPicks } from 'utils/system-composition-types';

export const NEW_EXISTING_EQUIPMENT_COMPONENT_TYPES = {
  'new-existing-condenser': 'Split System Condenser',
  'new-existing-furnace': 'Split System Furnace'
} as const;

// Exactly the modelData keys the server derivation consumes
// (derive-current-system.ts): deriveFuelFilter reads fuelType (Q1);
// componentRefrigerant reads refrigerantType > primaryRefrigerant >
// refrigerant > refrigerantCompatibility (Q3, array-or-string). Keys are
// in the key for the same reason structureSubtypeId is (cursor[bot]
// PR #4156): derivation-consumed inputs must move the derivation key, or
// a save that changes the derived context never refires the strips'
// context GET (AHA-6796 fuel-flip defect). Everything derivation does
// NOT read (heatingCapacity, blower CFM, notes, footprint) stays
// excluded — the LES-5 limitation now covers only derivation-inert specs.
const DERIVATION_SPEC_KEYS = [
  'fuelType',
  'refrigerantType',
  'primaryRefrigerant',
  'refrigerant',
  'refrigerantCompatibility'
] as const;

// null / undefined / '' / non-string junk all serialize to '' so a no-op
// save (absent -> null, etc.) cannot move the key. Arrays keep stored
// order — the server takes the FIRST non-empty entry, so order is a
// derivation input.
function derivationSpecValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.filter((v) => typeof v === 'string' && v !== '').join('+');
  }
  return typeof value === 'string' ? value : '';
}

// Comparator for "did anything derivation-relevant change" across
// saveBaseStructure payloads (AHA-6681 QA F2): the saved structure subtype
// plus per-system component fragments (component set + replacing flags +
// derivation-consumed spec fields) joined in system order. The autosave
// watcher diffs consecutive saved payloads with it to decide whether to
// bump equipmentSaveVersion (the strips' post-save context-refresh nonce).
// The subtype is included because the server's dwelling swap
// (swapFurnaceCodesForDwelling in get-recommendation-context.use-case.ts)
// sits upstream of the derivation fingerprint — a standard/mobile-home flip
// changes the derived context with zero component changes (AHA-6681,
// cursor[bot] PR #4156 finding). Derivation-consumed spec fields
// (DERIVATION_SPEC_KEYS above) ARE included (AHA-6796) — only
// derivation-inert spec/model edits remain the accepted LES-5 limitation
// on the Equipment Details step.
export function buildEquipmentDerivationKey(
  existingSystems: ExistingSystemType[],
  structureSubtypeId: number | null
): string {
  return `subtype=${structureSubtypeId};${existingSystems
    .map(
      (system) =>
        `${system.uniqueId}=${system.components
          .map(
            (component) =>
              `${component.uniqueId}:${
                component.replacing ? 1 : 0
              }:${DERIVATION_SPEC_KEYS.map((key) =>
                derivationSpecValue((component as never)[key])
              ).join('~')}`
          )
          .sort()
          .join(',')}`
    )
    .join(';')}`;
}

// Combustion-furnace conditional (AHA-6796): ventingMaterial only exists
// for fuel-burning furnaces (its options are gas-vent classes), so it is
// required exactly when fuelType is a combustion fuel. An LP furnace has
// a flue like NG, so propane counts — both 'Propane (LP)' (the live
// option) and bare 'Propane' from legacy modelData rows. An electric
// furnace has no flue — statically requiring ventingMaterial made
// electric furnaces permanently incomplete (the ducted Existing
// Equipment gate stuck at 0%). While fuelType is still unset the rule
// stays inert: fuelType itself is the required field blocking progress
// then.
const COMBUSTION_FUEL_TYPES = ['Natural Gas', 'Propane (LP)', 'Propane', 'Oil'];

// Structural parameter (not Pick): fuelType only exists on the spec-carrying
// members of the ExistingSystemComponentType union, so Pick<'fuelType'>
// doesn't compile — full components remain assignable either way.
export function combustionFurnaceConditionallyRequiredKeys(component: {
  type: ExistingSystemComponentType['type'] | undefined;
  fuelType?: string | null;
}): string[] {
  return component.type === 'Split System Furnace' &&
    COMBUSTION_FUEL_TYPES.includes(component.fuelType ?? '')
    ? ['ventingMaterial']
    : [];
}

export function combustionFurnaceMissingVentingMaterial(
  component: ExistingSystemComponentType
): boolean {
  return combustionFurnaceConditionallyRequiredKeys(component).some((key) =>
    isEmpty((component as never)[key])
  );
}

// The union of every conditional-requiredness rule (AHA-6796): the single
// source of truth shared by the page-level continue gate
// (componentHasMissingRequiredDetails below) and the spec-fields renderer's
// asterisk/partition — the modal and the page gate must never diverge on
// which fields are required. New conditional rules belong here so both
// consumers pick them up automatically.
export function conditionallyRequiredSpecKeys(component: {
  type: ExistingSystemComponentType['type'] | undefined;
  fuelType?: string | null;
}): string[] {
  return combustionFurnaceConditionallyRequiredKeys(component);
}

export function componentHasMissingRequiredDetails(
  component: ExistingSystemComponentType
): boolean {
  if (!component.type || component.typeSpecified === false) {
    return false;
  }
  const config = existingSystemComponentTypeProperties[component.type] ?? {};
  // Emptiness, not falsiness: a stored 0 (or false) satisfies a required
  // field — isEmpty treats only null/undefined/'' as missing.
  const missingStaticRequired = Object.entries(config).some(
    ([propKey, propConfig]) =>
      !!propConfig.required && isEmpty((component as never)[propKey])
  );
  if (missingStaticRequired) {
    return true;
  }
  return conditionallyRequiredSpecKeys(component).some((key) =>
    isEmpty((component as never)[key])
  );
}

export function componentsHaveRequiredAttributes(
  components: ExistingSystemComponentType[]
): boolean {
  return components.every(
    (component) => !componentHasMissingRequiredDetails(component)
  );
}

const CONDENSER_COMPONENT_TYPES = new Set<ExistingSystemComponentType['type']>([
  'Split System Condenser',
  'Split System Heat Pump'
]);

export type ExistingEquipmentPositionSlot = 'outdoor' | 'indoor' | 'blower';

// Mapping from existing-equipment component `type` (display string) to the
// compatibility position slot. Components with no mapping are dropped from
// the Phase 5 positions payload — only outdoor/indoor/blower slots feed the
// compatibility configuration tables — and carry no position into the
// synthetic ducted quote layout.
export const COMPONENT_TYPE_TO_POSITION: Partial<
  Record<ExistingSystemComponentType['type'], ExistingEquipmentPositionSlot>
> = {
  'Split System Condenser': 'outdoor',
  'Split System Heat Pump': 'outdoor',
  'Next-Gen Condenser': 'outdoor',
  'Next-Gen Heat Pump': 'outdoor',
  'Split System Evaporator Coil': 'indoor',
  'Split System Air Handler': 'indoor',
  'Next-Gen Evaporator Coil': 'indoor',
  'Next-Gen Air Handler': 'indoor',
  'Split System Furnace': 'blower',
  'Split System Modular Blower': 'blower'
};

export function keptEquipmentPositionSlots(
  components: ReadonlyArray<
    Pick<ExistingSystemComponentType, 'type' | 'replacing'>
  >
): Set<ExistingEquipmentPositionSlot> {
  const slots = new Set<ExistingEquipmentPositionSlot>();
  components.forEach((component) => {
    const slot = COMPONENT_TYPE_TO_POSITION[component.type];
    if (slot && !component.replacing) {
      slots.add(slot);
    }
  });
  return slots;
}

// The four packaged (Self Contained) component types (AHA-6796). PTAC is
// deliberately excluded — it is not offered on the ducted path. Single
// source of truth for every SC consumer: reducer targeting, modal category
// filtering, the details-card type dropdown, and the always-replacing
// treatment.
export const SELF_CONTAINED_COMPONENT_TYPES = new Set<
  ExistingSystemComponentType['type']
>([
  'Self Contained Electric AC',
  'Self Contained Electric Heat Pump',
  'Self Contained Gas Pack With AC',
  'Self Contained Gas Pack With Heat Pump'
]);

export function isSelfContainedComponentType(
  type: ExistingSystemComponentType['type'] | undefined
): boolean {
  return !!type && SELF_CONTAINED_COMPONENT_TYPES.has(type);
}

// The single definition of "an SC type change that must be refused"
// (AHA-6796, cursor[bot] PR #4330): a component with siblings must not
// CHANGE its type to a packaged (Self Contained) one — an SC unit is a
// complete standalone system, so the change would mix it into a split
// system. The same-type carve-out (newType === currentType) keeps legacy
// mixed rows re-assertable: an edit of a legacy SC component that already
// has siblings re-asserts its own type and introduces no new mixing.
// Converting AWAY from SC (a split target) is deliberately never blocked —
// that direction reduces mixing. Shared by the
// updateExistingSystemComponent reducer's strip guard, the modal's tier-2
// kind filter, and the modal's save guard — the consumers must never
// diverge on which type changes are refused, or a picker could offer a
// change the reducer silently strips (the PR #4330 corruption:
// clearedOldTypeKeys/specPayload landing on an unchanged type).
export function isBlockedSelfContainedTypeChange(
  newType: ExistingSystemComponentType['type'] | undefined,
  currentType: ExistingSystemComponentType['type'] | undefined,
  hasSiblings: boolean
): boolean {
  return (
    hasSiblings &&
    isSelfContainedComponentType(newType) &&
    newType !== currentType
  );
}

export function systemHasSelfContainedComponent(
  system: ExistingSystemType
): boolean {
  return system.components.some((component) =>
    isSelfContainedComponentType(component.type)
  );
}

export function existingSystemAcceptsComponentKind(
  system: ExistingSystemType,
  isSelfContained: boolean
): boolean {
  if (system.type !== 'ducted') return false;
  return isSelfContained
    ? system.components.length === 0
    : !systemHasSelfContainedComponent(system);
}

export function existingSystemAcceptsComponentType(
  system: ExistingSystemType,
  type: ExistingSystemComponentType['type'] | undefined
): boolean {
  return existingSystemAcceptsComponentKind(
    system,
    isSelfContainedComponentType(type)
  );
}

// "Does this component share its system with other components?" — the
// derivation every SC mixing guard keys off (AHA-6796, cursor[bot] PR #4330
// finding): a component with siblings must never carry a packaged (Self
// Contained) type, because an SC unit is a complete standalone system.
// Single source of truth for the modal's category withholding, the details
// form's type-option filter, the card auto-analysis sanitize step, and the
// updateExistingSystemComponent reducer guard — the consumers must never
// diverge on what "has siblings" means.
export function componentHasSiblingsInSystem(
  existingSystems: ExistingSystemType[],
  componentUniqueId: string
): boolean {
  return existingSystems.some(
    (system) =>
      system.components.length > 1 &&
      system.components.some(
        (component) => component.uniqueId === componentUniqueId
      )
  );
}

const COMPONENT_GRID_LABELS: Partial<
  Record<ExistingSystemComponentType['type'], string>
> = {
  'Split System Condenser': 'C',
  'Split System Heat Pump': 'HP',
  'Split System Furnace': 'F',
  'Split System Evaporator Coil': 'EC',
  'Split System Air Handler': 'AH',
  'Split System Modular Blower': 'MB',
  'Self Contained Electric AC': 'SC',
  'Self Contained Electric Heat Pump': 'SC',
  'Self Contained Gas Pack With AC': 'SC',
  'Self Contained Gas Pack With Heat Pump': 'SC'
};

export function clearAnalyzingFlags(
  existingSystems: ExistingSystemType[]
): ExistingSystemType[] {
  return existingSystems.map((system) => ({
    ...system,
    components: system.components.map((component) => ({
      ...component,
      analyzing: false
    }))
  }));
}

export function isExistingCondenserComponent(
  component: ExistingSystemComponentType
): boolean {
  return CONDENSER_COMPONENT_TYPES.has(component.type);
}

export function getExistingEquipmentComponents(
  existingSystems: ExistingSystemType[]
): ExistingSystemComponentType[] {
  return existingSystems.flatMap((system) => system.components);
}

export function isExistingEquipmentComponentPlaced(
  component: ExistingSystemComponentType
): boolean {
  return !!component.position && !!component.levelUniqueId;
}

// Placement-filtered: feeds Layout & Sizing grid rendering — all other
// consumers treat null placement as first-class.
export function getPlacedExistingEquipmentComponents(
  existingSystems: ExistingSystemType[]
): ExistingSystemComponentType[] {
  return getExistingEquipmentComponents(existingSystems).filter(
    isExistingEquipmentComponentPlaced
  );
}

// The Existing Equipment strip-completion predicate: wantIds present — wants
// auto-seed on first derivation, and in the tiles variant they are also
// manually selectable with no have at all (AHA-6681 amendment B: want-only
// selection is a first-class completed path). The
// SINGLE definition shared by the page-progress watcher's ducted EE gate and
// the Choose Systems eligibility selector below — the two must never diverge
// or the tab could unlock a page that renders empty (or vice versa).
// The argument shape matches the structure slice's per-system mirror
// (recommendationSelectionsBySystem); null = "context fetched, nothing
// saved", undefined = no entry.
export function isRecommendationStripCompleted(
  selections: { wantIds: number[] } | null | undefined,
  picks?: CompositionPicks | null
): boolean {
  return (
    (!!selections && selections.wantIds.length > 0) ||
    compositionHasEquipmentType(picks)
  );
}

// Ducted systems eligible for the Choose Systems pipeline (mount gate,
// compatible-combinations fetches, rail/filters/detail surfaces): any ducted
// system with at least one component, OR a zero-component system whose
// recommendations strip is completed — virtual-tile haves live on the
// zero-component anchor row (AHA-6681 no-equipment strip) and must still
// drive Choose Systems. Placement is deliberately NOT required — equipment
// entered on the Existing Equipment page saves with null placement and must
// still drive the fetches. The PTAC anchor never qualifies: its rooms are
// matched per unit off the System Design (features/ptac-selection).
export function getChooseSystemsEligibleDuctedSystems(
  existingSystems: ExistingSystemType[],
  recommendationSelectionsBySystem: Record<
    string,
    { wantIds: number[] } | null
  >,
  compositionPicksBySystem: Record<string, CompositionPicks> = {}
): ExistingSystemType[] {
  return existingSystems.filter(
    (system) =>
      system.type === 'ducted' &&
      (system.components.length > 0 ||
        isRecommendationStripCompleted(
          recommendationSelectionsBySystem[system.uniqueId],
          compositionPicksBySystem[system.uniqueId]
        ))
  );
}

// Reference-stable results for the Fuel seed below: the reducer compares
// nothing, but memo consumers and the pre-warm hash parity like stable
// shapes.
const NO_SEED_FUELS: DuctedFuelToken[] = [];
const LP_SEED_FUELS: DuctedFuelToken[] = ['lp'];

// Propane spellings: 'Propane (LP)' is the live fuelType option; bare
// 'Propane' rides legacy modelData rows (same tolerance as
// COMBUSTION_FUEL_TYPES above).
const PROPANE_FUEL_TYPES = ['Propane (LP)', 'Propane'];

// The Fuel chip seed for the Choose Systems ducted filters (AHA-6796
// Decision B): ['lp'] when ANY furnace component (kept or replacing) in an
// eligible ducted system burns propane, OR a tile-mode project saved
// virtualFuel 'LP' (zero-component systems carry their fuel in the
// recommendation selections record, not on a component) — AND the FULL
// eligible universe actually offers an LP system (a seed with no lp code
// in the universe would be an invisible filter: no Propane chip renders,
// so nothing is clearable — renderability IS universe membership since the
// fuel-vocabulary widening). Other fuels never seed. Structural component
// param (fuelType?: string | null) — fuelType only exists on the
// spec-carrying members of the component union.
export function ductedSeedFuels(
  eligibleSystems: Array<{
    uniqueId: string;
    components: Array<{
      type: ExistingSystemComponentType['type'] | undefined;
      fuelType?: string | null;
    }>;
  }>,
  recommendationSelectionsBySystem: Record<
    string,
    { virtualFuel?: string | null } | null | undefined
  >,
  // The full eligible universe (ductedUniverseCodes at both call sites —
  // the reset hook and the pre-warm builder), so the seed guard keys on
  // exactly the chip row's renderability.
  universeCodes: string[]
): DuctedFuelToken[] {
  const propane = eligibleSystems.some(
    (system) =>
      system.components.some(
        (component) =>
          component.type === 'Split System Furnace' &&
          PROPANE_FUEL_TYPES.includes(component.fuelType ?? '')
      ) ||
      recommendationSelectionsBySystem[system.uniqueId]?.virtualFuel === 'LP'
  );
  return propane &&
    universeCodes.some((code) => systemTypeCodeFuel(code) === 'lp')
    ? LP_SEED_FUELS
    : NO_SEED_FUELS;
}

// The honest-source description behind a seeded Propane selection
// (AHA-6796 EE fuel row): names the matched propane furnace (with its
// model number when present) or the tile-mode virtualFuel 'LP' save —
// exactly the ductedSeedFuels match sources, kept next to it so the hint
// and the seed cannot diverge. Null when nothing propane rides the
// eligible systems (the row then renders no equipment hint).
export function ductedPropaneSeedDescription(
  eligibleSystems: Array<{
    uniqueId: string;
    components: Array<{
      type: ExistingSystemComponentType['type'] | undefined;
      fuelType?: string | null;
      modelNumber?: string | null;
    }>;
  }>,
  recommendationSelectionsBySystem: Record<
    string,
    { virtualFuel?: string | null } | null | undefined
  >
): string | null {
  for (const system of eligibleSystems) {
    const furnace = system.components.find(
      (component) =>
        component.type === 'Split System Furnace' &&
        PROPANE_FUEL_TYPES.includes(component.fuelType ?? '')
    );
    if (furnace) {
      return furnace.modelNumber
        ? `your furnace (${furnace.modelNumber}) is propane`
        : 'your furnace is propane';
    }
  }
  const virtual = eligibleSystems.some(
    (system) =>
      recommendationSelectionsBySystem[system.uniqueId]?.virtualFuel === 'LP'
  );
  return virtual ? 'your furnace is propane' : null;
}

// Reference-stable empty result for the chassis seed below (fuel-seed
// precedent): the pre-warm builder and the dispatch memo like stable shapes.
const NO_SEED_CHASSIS_SIZES: string[] = [];

// The Cabinet / Chassis Size seed for the Choose Systems ducted spec
// filters (AHA-6796): the union of every uploaded component's
// cabinetChassisSize across the eligible ducted systems — kept OR replaced
// (`replacing` is deliberately irrelevant; Geoff ruling, same as
// ductedSeedFuels). Exactly the uploaded size(s), no expansion. Values are
// filtered to the plain letter codes (CHASSIS_SIZE_OPTIONS) so legacy
// decorated modelData strings ('17.5"') never seed, and sorted for
// determinism. A system whose want composition is a packaged unit
// contributes nothing — packaged units have no chassis size. Structural
// component param — cabinetChassisSize only exists on the spec-carrying
// members of the component union.
export function ductedSeedChassisSizes(
  eligibleSystems: Array<{
    uniqueId: string;
    components: Array<{
      type: ExistingSystemComponentType['type'] | undefined;
      cabinetChassisSize?: string | null;
    }>;
  }>,
  compositionPicksBySystem: Record<string, CompositionPicks> = {}
): string[] {
  const sizes = new Set<string>();
  eligibleSystems
    .filter(
      (system) =>
        !compositionWantsPackagedUnit(compositionPicksBySystem[system.uniqueId])
    )
    .forEach((system) => {
      system.components.forEach((component) => {
        const size = component.cabinetChassisSize;
        if (size && CHASSIS_SIZE_OPTIONS.includes(size)) {
          sizes.add(size);
        }
      });
    });
  return sizes.size === 0 ? NO_SEED_CHASSIS_SIZES : Array.from(sizes).sort();
}

// Reference-stable empty result for the orientation seed below (chassis-
// seed precedent).
const NO_SEED_ORIENTATIONS: string[] = [];

// The existing-equipment orientation vocabulary (ORIENTATION_OPTIONS,
// lowercase) mapped onto the pnet orientation facet's values (the spec
// tables' Upflow / Downflow / Horizontal airflow bits), in the facet's
// canonical order. Keyed lowercase so analyzer-written casing variants
// ('Upflow', 'UPFLOW') seed too; anything else never seeds.
const ORIENTATION_FACET_VALUES: ReadonlyArray<[string, string]> = [
  ['upflow', 'Upflow'],
  ['downflow', 'Downflow'],
  ['horizontal', 'Horizontal']
];

// The Orientation seed for the Choose Systems ducted spec filters: the
// union of every uploaded component's orientation across the eligible
// ducted systems — kept OR replaced (`replacing` is deliberately
// irrelevant, the chassis-seed ruling): the ductwork dictates the airflow
// direction whether the furnace, air handler, or coil is the piece being
// replaced. A compound legacy value ('upflow/horizontal') seeds each
// direction it names. Structural component param — orientation only
// exists on the spec-carrying members of the component union.
export function ductedSeedOrientations(
  eligibleSystems: Array<{
    components: Array<{
      type: ExistingSystemComponentType['type'] | undefined;
      orientation?: string | null;
    }>;
  }>
): string[] {
  const named = new Set<string>();
  eligibleSystems.forEach((system) => {
    system.components.forEach((component) => {
      (component.orientation ?? '')
        .split(/[/,]/)
        .map((token) => token.trim().toLowerCase())
        .filter((token) => token.length > 0)
        .forEach((token) => named.add(token));
    });
  });
  const seeded = ORIENTATION_FACET_VALUES.filter(([option]) =>
    named.has(option)
  ).map(([, facetValue]) => facetValue);
  return seeded.length === 0 ? NO_SEED_ORIENTATIONS : seeded;
}

export function everyDuctedSystemWantsPackagedUnit(
  eligibleSystems: Array<{ uniqueId: string }>,
  compositionPicksBySystem: Record<string, CompositionPicks>
): boolean {
  return (
    eligibleSystems.length > 0 &&
    eligibleSystems.every((system) =>
      compositionWantsPackagedUnit(compositionPicksBySystem[system.uniqueId])
    )
  );
}

// Placement-filtered variant for Layout & Sizing grid rendering of placed
// panels; the placement step's left panel uses getAllElectricalPanels.
export function getPlacedElectricalPanels(
  structure: LevelType[]
): { panel: ElectricalPanelType; levelLabel: string }[] {
  return structure.flatMap((level) =>
    level.electricalPanels
      .filter((panel) => !!panel.position)
      .map((panel) => ({ panel, levelLabel: level.label }))
  );
}

// All panels regardless of placement — the Existing Equipment page renders
// every panel (null placement is first-class there).
export function getAllElectricalPanels(
  structure: LevelType[]
): { panel: ElectricalPanelType; levelLabel: string }[] {
  return structure.flatMap((level) =>
    level.electricalPanels.map((panel) => ({ panel, levelLabel: level.label }))
  );
}

export function hasPlaceableExistingEquipment(
  existingSystems: ExistingSystemType[],
  structure: LevelType[]
): boolean {
  return (
    getExistingEquipmentComponents(existingSystems).length > 0 ||
    getAllElectricalPanels(structure).length > 0
  );
}

export function getExistingEquipmentCategoryLabel(
  component: ExistingSystemComponentType
): string {
  return isExistingCondenserComponent(component)
    ? 'Outdoor Unit'
    : 'Indoor Unit';
}

function getExistingEquipmentGridCategoryLabel(
  component: ExistingSystemComponentType
): string {
  return isExistingCondenserComponent(component) ? 'OUT' : 'IN';
}

export function getExistingEquipmentTypeLabel(
  component: ExistingSystemComponentType
): string {
  if (component.typeSpecified === false) {
    return getExistingEquipmentCategoryLabel(component);
  }
  return component.type && component.type !== 'unknown'
    ? component.type
    : 'Existing Equipment';
}

export function getExistingEquipmentTooltip(
  component: ExistingSystemComponentType
): string {
  return `${getExistingEquipmentTypeLabel(component)} (${
    component.replacing ? 'Replacing' : 'Keeping'
  })`;
}

// The short COMPONENT_GRID_LABELS icon text ('C', 'HP', 'F', ...) — shared
// by the placed grid objects and the placement-step drag previews.
export function getExistingEquipmentGridLabel(
  component: ExistingSystemComponentType
): string {
  return component.typeSpecified === false
    ? getExistingEquipmentGridCategoryLabel(component)
    : COMPONENT_GRID_LABELS[component.type] ?? 'EQ';
}

export function getExistingEquipmentGridObjects(
  existingSystems: ExistingSystemType[],
  levelUniqueId: string
): GridObjectType[] {
  return getPlacedExistingEquipmentComponents(existingSystems)
    .filter((component) => component.levelUniqueId === levelUniqueId)
    .map((component) => ({
      id: component.uniqueId,
      itemId: component.uniqueId,
      position: {
        x: component.position!.x,
        y: component.position!.y,
        width: 4,
        height: 4
      },
      type: isExistingCondenserComponent(component)
        ? 'existing-condenser'
        : 'existing-furnace',
      label: getExistingEquipmentGridLabel(component),
      levelUniqueId,
      tooltip: getExistingEquipmentTooltip(component)
    }));
}
